import { useState, useEffect, useCallback, useRef } from 'react';
import type { CookiePreferences } from '@/types/cookieConsent';
import {
  getCookiePreferences,
  saveCookiePreferences,
  hasConsentDecision,
  isCategoryEnabled,
  initializeWithGPC,
  doNotSell as doNotSellUtil,
  COOKIE_CONSENT_VERSION,
  hasGlobalPrivacyControl,
} from '@/lib/cookieConsent';

export function useCookieConsent() {
  const [preferences, setPreferences] = useState<CookiePreferences | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Track whether the banner was showing before opening preferences,
  // so we can restore it if the user cancels without saving.
  const bannerWasVisibleRef = useRef(false);

  // Initialize on mount
  useEffect(() => {
    const storedPreferences = getCookiePreferences();
    
    if (storedPreferences) {
      setPreferences(storedPreferences);
      setShowBanner(false);
    } else {
      // Check for GPC
      if (hasGlobalPrivacyControl()) {
        const gpcPreferences = initializeWithGPC();
        saveCookiePreferences(gpcPreferences);
        setPreferences(gpcPreferences);
        setShowBanner(false); // Don't show banner if GPC is honored
      } else {
        // Show banner to new users
        setShowBanner(true);
      }
    }
    
    setIsInitialized(true);
  }, []);

  const acceptAll = useCallback(() => {
    const newPreferences: CookiePreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: Date.now(),
      version: COOKIE_CONSENT_VERSION,
      gpcHonored: false,
      method: 'banner_accept',
    };
    
    saveCookiePreferences(newPreferences);
    setPreferences(newPreferences);
    setShowBanner(false);
    setShowPreferences(false);
  }, []);

  const rejectAll = useCallback(() => {
    const newPreferences: CookiePreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: Date.now(),
      version: COOKIE_CONSENT_VERSION,
      gpcHonored: false,
      method: 'banner_reject',
    };
    
    saveCookiePreferences(newPreferences);
    setPreferences(newPreferences);
    setShowBanner(false);
    setShowPreferences(false);
  }, []);

  const updatePreferences = useCallback((newPrefs: Partial<CookiePreferences>) => {
    const updatedPreferences: CookiePreferences = {
      necessary: true,
      analytics: newPrefs.analytics ?? preferences?.analytics ?? false,
      marketing: newPrefs.marketing ?? preferences?.marketing ?? false,
      timestamp: Date.now(),
      version: COOKIE_CONSENT_VERSION,
      gpcHonored: false,
      method: 'preferences_save',
    };
    
    saveCookiePreferences(updatedPreferences);
    setPreferences(updatedPreferences);
    setShowBanner(false);
    setShowPreferences(false);
  }, [preferences]);

  const openPreferences = useCallback(() => {
    // Remember if the banner was showing so we can restore it on cancel
    bannerWasVisibleRef.current = showBanner;
    setShowPreferences(true);
    // Keep banner hidden while preferences dialog is open to avoid overlap
    setShowBanner(false);
  }, [showBanner]);

  const closePreferences = useCallback(() => {
    setShowPreferences(false);
    // If the user closes preferences without saving, and the banner was
    // previously visible, re-show it so they can't escape consent.
    if (bannerWasVisibleRef.current && !preferences) {
      setShowBanner(true);
    }
  }, [preferences]);

  const hasConsent = useCallback((category: 'analytics' | 'marketing') => {
    return isCategoryEnabled(category);
  }, []);

  const doNotSell = useCallback(() => {
    doNotSellUtil();
    const newPreferences = getCookiePreferences();
    setPreferences(newPreferences);
    setShowBanner(false);
    setShowPreferences(false);
  }, []);

  const reset = useCallback(() => {
    localStorage.removeItem('shoresight_cookie_consent');
    setPreferences(null);
    setShowBanner(true);
    setShowPreferences(false);
  }, []);

  return {
    preferences,
    showBanner,
    showPreferences,
    isInitialized,
    hasConsent,
    acceptAll,
    rejectAll,
    updatePreferences,
    openPreferences,
    closePreferences,
    doNotSell,
    reset,
  };
}
